import dayjs, { type Dayjs } from 'dayjs';

export type HolidayShiftType = 'whole_day' | 'morning' | 'evening';

export interface CompanyHoliday {
  date: string; // YYYY-MM-DD
  shift: HolidayShiftType;
  description?: string;
}

// Morning shift is 00:00 to 5:00 PM (17:00 = 1020 minutes)
// Evening shift is 5:01 PM to 11:59 PM (17:01 = 1021 minutes)
export const MORNING_END_MINUTES = 17 * 60; // 1020
export const EVENING_START_MINUTES = 17 * 60 + 1; // 1021

export function parseHolidays(raw: string | null | undefined): CompanyHoliday[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((h: any) => ({
      date: h.date,
      shift: h.shift as HolidayShiftType,
      description: h.description,
    }));
  } catch {
    return [];
  }
}

export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function getHolidayForDate(holidays: CompanyHoliday[], date: Dayjs | string): CompanyHoliday | undefined {
  if (!holidays || holidays.length === 0) return undefined;
  const dateStr = typeof date === 'string' ? dayjs(date).format('YYYY-MM-DD') : date.format('YYYY-MM-DD');
  const matches = holidays.filter((h) => h.date === dateStr);
  if (matches.length === 0) return undefined;

  // If any entry is whole_day, or both morning and evening are off, it's whole_day
  if (matches.some((h) => h.shift === 'whole_day')) {
    return matches.find((h) => h.shift === 'whole_day');
  }

  const hasMorning = matches.some((h) => h.shift === 'morning');
  const hasEvening = matches.some((h) => h.shift === 'evening');
  if (hasMorning && hasEvening) {
    return {
      date: dateStr,
      shift: 'whole_day',
      description:
        matches
          .map((m) => m.description)
          .filter(Boolean)
          .join(', ') || 'Holiday (Full Day)',
    };
  }

  return matches[0];
}

export function isDateWholeDayHoliday(holidays: CompanyHoliday[], date: Dayjs | string): boolean {
  const holiday = getHolidayForDate(holidays, date);
  return holiday?.shift === 'whole_day';
}

export function isShiftAllowedByHoliday(
  shift: { startTime: string; endTime: string },
  holiday?: CompanyHoliday,
): boolean {
  if (!holiday) return true;
  if (holiday.shift === 'whole_day') return false;

  const startMinutes = parseTimeToMinutes(shift.startTime);
  const endMinutes = parseTimeToMinutes(shift.endTime);
  const isOvernight = endMinutes < startMinutes;

  if (holiday.shift === 'morning') {
    // Morning shift is off (00:00 to 17:00).
    // Hide any shift that starts before 5:00 PM (17:00).
    return startMinutes >= MORNING_END_MINUTES;
  }

  if (holiday.shift === 'evening') {
    // Evening shift is off (17:01 to 23:59).
    // Hide any shift that ends or starts after 5:00 PM.
    // Overnight shifts running past midnight also span through the evening.
    if (isOvernight) return false;
    return startMinutes <= MORNING_END_MINUTES && endMinutes <= MORNING_END_MINUTES;
  }

  return true;
}

export function filterShiftsByHoliday<T extends { startTime: string; endTime: string }>(
  shifts: T[],
  holiday?: CompanyHoliday,
): T[] {
  if (!holiday) return shifts;
  return shifts.filter((shift) => isShiftAllowedByHoliday(shift, holiday));
}

export function getHolidayMessage(holiday?: CompanyHoliday): string {
  if (!holiday) return '';
  const holidayLabel = holiday.description ? `holiday "${holiday.description}"` : 'holiday "Project Holiday"';

  if (holiday.shift === 'whole_day') {
    return `This date is a project holiday "${holiday.description || 'Whole Day'}". Scheduling is not allowed.`;
  }
  if (holiday.shift === 'morning') {
    return `Morning shift (00:00 – 5:00 PM) is off for ${holidayLabel}. Schedules must start at or after 5:00 PM.`;
  }
  if (holiday.shift === 'evening') {
    return `Evening shift (after 5:00 PM) is off for ${holidayLabel}. Schedules cannot start or end after 5:00 PM.`;
  }
  return '';
}

export function isCustomTimeAllowedByHoliday(
  startTime: Dayjs,
  endTime: Dayjs,
  holiday?: CompanyHoliday,
): { allowed: boolean; message?: string } {
  if (!holiday) return { allowed: true };

  const message = getHolidayMessage(holiday);

  if (holiday.shift === 'whole_day') {
    return { allowed: false, message };
  }

  const startMinutes = startTime.hour() * 60 + startTime.minute();
  const endMinutes = endTime.hour() * 60 + endTime.minute();
  const isOvernight = endTime.isAfter(startTime, 'day') || endMinutes < startMinutes;

  if (holiday.shift === 'morning') {
    if (startMinutes < MORNING_END_MINUTES) {
      return { allowed: false, message };
    }
  }

  if (holiday.shift === 'evening') {
    if (startMinutes > MORNING_END_MINUTES || endMinutes > MORNING_END_MINUTES || isOvernight) {
      return { allowed: false, message };
    }
  }

  return { allowed: true };
}
